import React, {CSSProperties, MutableRefObject, ReactNode} from 'react'

import CurrencyInput from '../forms/CurrencyInput.js'
import {Check, DatePicker, FormRow, Input, MetricInput, RadioButton, Select, TimePicker} from '../forms/index.js'
import type {DatePickerChangeEvent} from '../forms/DatePicker.js'

export type PopupInputValues = Record<string, unknown>

export interface PopupInput {
    type: 'date' | 'time' | 'text' | 'number' | 'select' | 'currency' | 'metric' | 'checkbox' | 'radio' | 'custom'
    /** Key of the value in the object open() resolves with */
    name?: string
    label?: string
    value?: any
    /** Multiple select */
    values?: any[]
    /** A function gets the current values of all inputs (by name) */
    disabled?: boolean | ((values: PopupInputValues) => boolean)
    /** Confirming needs a value */
    required?: boolean
    /** Consecutive inputs with the same row are placed next to each other */
    row?: string | number
    /** radio: the value of the group (inputs with the same name) when this one is checked */
    optionValue?: any
    style?: CSSProperties
    info?: ReactNode
    // date
    minDate?: string
    maxDate?: string
    // time
    duration?: boolean | 'seconds'
    // number, metric
    min?: number
    max?: number
    // metric
    divisionFactor?: number
    append?: string
    // select
    options?: any[]
    noSort?: boolean
    selectAll?: boolean
    allowEmpty?: boolean
    placeholder?: string
    // custom: a control the Popup doesn't know (e.g. LabelStartPicker), its value is confirmed like the others
    render?: (value: any, onChange: (value: any) => void, options: {disabled: boolean}) => ReactNode
}

/** Controls that can take the focus when a Popup opens (not checkboxes, radios and time pickers, as before) */
export const autoFocusTypes: PopupInput['type'][] = ['text', 'number', 'date', 'select', 'currency', 'metric']

/** The focus handles of the rendered inputs, by index; Select's focused() tells whether its list is open */
export type PopupInputRefs = MutableRefObject<({focus?: () => void, focused?: () => boolean} | null)[]>

interface PopupInputsProps {
    inputs: PopupInput[]
    loading?: boolean
    onChange: (index: number, value: any) => void
    inputRefs: PopupInputRefs
}

/** Flat {name: value} map of the inputs: what open() resolves with, and what a disabled function gets */
export function getInputValues(inputs: PopupInput[] = []) {
    return inputs.reduce<PopupInputValues>((values, input) => {
        values[input.name] = input.values || input.value

        return values
    }, {})
}

/** Whether an input marked required has no value, then the Popup can't be confirmed */
export function isRequiredInputMissing(inputs: PopupInput[] = []) {
    return inputs.some((input) => input.required && (Array.isArray(input.values) ? !input.values.length : !String(input.value ?? '').trim()))
}

// Groups consecutive inputs sharing the same `row` into one row (e.g. a radio paired with the field it enables), so
// callers can lay out dependent fields side by side without Popup knowing what they mean
function groupInputRows(inputs: PopupInput[]) {
    const rows: {row: PopupInput['row'], items: {input: PopupInput, index: number}[]}[] = []

    inputs.forEach((input, index) => {
        const lastRow = rows[rows.length - 1]

        if (input.row && lastRow?.row === input.row) {
            lastRow.items.push({input, index})
        } else {
            rows.push({row: input.row, items: [{input, index}]})
        }
    })

    return rows
}

/** The inputs of a Popup (setInputs, or the single setInput), one per row unless they share a `row` */
export default function PopupInputs({inputs, loading, onChange, inputRefs}: PopupInputsProps) {
    const values = getInputValues(inputs)

    const isDisabled = (input: PopupInput) => loading || (typeof input.disabled === 'function' ? input.disabled(values) : !!input.disabled)

    const renderInput = (input: PopupInput, index: number) => {
        const key = `input${index}`
        const common = {
            label: input.label,
            disabled: isDisabled(input)
        }
        const setRef = (ref: PopupInputRefs['current'][number]) => {
            inputRefs.current[index] = ref
        }

        switch (input.type) {
            case 'date':
                return (
                    <DatePicker
                        key={key}
                        {...common}
                        value={input.value}
                        minDate={input.minDate}
                        maxDate={input.maxDate}
                        // Not a range picker, so always an event
                        onChange={(event) => onChange(index, (event as DatePickerChangeEvent).target.value)}
                        ref={setRef}
                    />
                )
            case 'time':
                return (
                    <TimePicker
                        key={key}
                        {...common}
                        value={input.value}
                        onChange={(event) => onChange(index, input.duration ? +event.target.value : event.target.value)}
                        duration={input.duration}
                    />
                )
            case 'text':
                return (
                    <Input
                        key={key}
                        {...common}
                        value={input.value}
                        onChange={(event) => onChange(index, event.target.value)}
                        style={input.style}
                        ref={setRef}
                    />
                )
            case 'number':
                return (
                    <Input
                        key={key}
                        {...common}
                        value={input.value}
                        onChange={(event) => onChange(index, event.target.value)}
                        type='number'
                        min={input.min}
                        max={input.max}
                        style={input.style}
                        ref={setRef}
                    />
                )
            case 'select':
                return (
                    <Select
                        key={key}
                        {...common}
                        noSort={input.noSort}
                        selectAll={input.selectAll}
                        info={input.info}
                        value={input.value}
                        values={input.values}
                        options={input.options}
                        allowEmpty={input.allowEmpty}
                        placeholder={input.placeholder}
                        onChange={(event) => onChange(index, event.target.values || event.target.value)}
                        style={input.style}
                        ref={setRef}
                    />
                )
            case 'currency':
                return (
                    <CurrencyInput
                        key={key}
                        {...common}
                        info={input.info}
                        value={input.value}
                        onChange={(event) => onChange(index, event.target.value)}
                        style={input.style}
                        ref={setRef}
                    />
                )
            case 'metric':
                return (
                    <MetricInput
                        key={key}
                        {...common}
                        info={input.info}
                        value={input.value}
                        divisionFactor={input.divisionFactor}
                        min={input.min}
                        max={input.max}
                        append={input.append}
                        onChange={(event) => onChange(index, event.target.value)}
                        style={input.style}
                        ref={setRef}
                    />
                )
            case 'checkbox':
                return (
                    <Check
                        key={key}
                        {...common}
                        checked={input.value}
                        onChange={(event) => onChange(index, event.target.checked)}
                        style={input.style}
                    />
                )
            case 'radio':
                return (
                    <RadioButton
                        key={key}
                        {...common}
                        info={input.info}
                        checked={input.value === input.optionValue}
                        value={input.optionValue}
                        onChange={(event) => onChange(index, event.target.value)}
                        style={input.style}
                    />
                )
            case 'custom':
                return (
                    <React.Fragment key={key}>
                        {input.render?.(input.value, (value) => onChange(index, value), {disabled: common.disabled})}
                    </React.Fragment>
                )
            default:
                return null
        }
    }

    return (
        <>
            {groupInputRows(inputs).map((rowGroup, rowIndex) => {
                if (rowGroup.items.length === 1) {
                    const {input, index} = rowGroup.items[0]

                    return renderInput(input, index)
                }

                return (
                    <FormRow key={`inputRow${rowIndex}`}>
                        {rowGroup.items.map(({input, index}) => renderInput(input, index))}
                    </FormRow>
                )
            })}
        </>
    )
}
